import { useState, useCallback, useEffect, useRef } from 'react';
import { Button, Select, Grid, Card, Stack, Inline } from '../components/atoms';
import { Text } from '../components/atoms/Typography/Text';
import { useTranslation } from '../i18n/useTranslation';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Fish } from 'lucide-react';
import { EmptyState } from '../components/atoms/EmptyState';
import { LivestockList } from '../components/livestock/LivestockList';
import { LivestockForm, type LivestockFormSuccessOptions } from '../components/livestock/LivestockForm';
import { QuarantinePromptDialog } from '../components/quarantine/QuarantinePromptDialog';
import type { QuarantineAnimal } from '../components/quarantine/StartQuarantineForm';
import { MarkDeceasedDialog } from '../components/livestock/MarkDeceasedDialog';
import { LivestockIdentifyFlow } from '../components/livestock/LivestockIdentifyFlow/LivestockIdentifyFlow';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import {
  useLivestockList,
  useDeleteLivestock,
} from '../hooks/useLivestock';
import { patchLivestock } from '../services/livestockService';
import type { LivestockRecord } from '../services/livestockService';
import { useToast } from '../stores/toastStore';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { Alert } from '../components/atoms/Alert/Alert';
import type { OverviewResultState } from './LivestockOverviewPage';

export function LivestockPage() {
  const { t } = useTranslation();
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  // Summary after "Analyse an overview photo" created animals (navigation state).
  const overviewResult = (location.state as Partial<OverviewResultState> | null)?.overviewResult ?? null;
  const dismissOverviewResult = useCallback(() => {
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, navigate]);
  const openOverview = useCallback(() => {
    navigate(`/aquariums/${aquariumId}/livestock/overview`);
  }, [aquariumId, navigate]);

  const [categoryFilter, setCategoryFilter] = useState<
    'coral' | 'fish' | 'invertebrate' | 'plant' | 'other' | ''
  >('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'archived' | 'deceased' | 'quarantine' | ''>('');
  const [sortFilter, setSortFilter] = useState<'date_added' | 'common_name' | 'category' | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<LivestockRecord | null>(null);
  const [isIdentifyOpen, setIsIdentifyOpen] = useState(false);
  const [deceasedTarget, setDeceasedTarget] = useState<LivestockRecord | null>(null);
  const identifyButtonRef = useRef<HTMLButtonElement>(null);
  // `?identify=1` (e.g. "Try it now" in the onboarding tour) opens "Identify from
  // photo" directly; the parameter is consumed so a reload does not reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const identifyRequested = searchParams.get('identify') === '1';
  useEffect(() => {
    if (!identifyRequested) return;
    setIsIdentifyOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('identify');
    setSearchParams(next, { replace: true });
  }, [identifyRequested, searchParams, setSearchParams]);

  const listParams = {
    ...(categoryFilter
      ? { category: categoryFilter as 'coral' | 'fish' | 'invertebrate' | 'other' }
      : {}),
    ...(statusFilter ? { status: statusFilter as 'active' | 'archived' | 'deceased' | 'quarantine' } : {}),
    ...(sortFilter ? { sort: sortFilter as 'date_added' | 'common_name' | 'category' } : {}),
  };

  const { data, isLoading, isError, refetch } = useLivestockList(aquariumId, listParams);
  const deleteMutation = useDeleteLivestock(aquariumId);

  const handleAdd = useCallback(() => {
    setEditingRecord(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((record: LivestockRecord) => {
    setEditingRecord(record);
    setFormOpen(true);
  }, []);

  const handleDelete = useCallback((record: LivestockRecord) => {
    deleteMutation.mutateAsync(record.id).catch(() => {
      showToast(t('livestock.deleteError'));
    });
  }, [deleteMutation, showToast, t]);

  const handleArchive = useCallback((record: LivestockRecord) => {
    patchLivestock(aquariumId, record.id, { status: 'archived' })
      .then(() => refetch())
      .catch(() => {
        showToast(t('livestock.archiveError'));
      });
  }, [aquariumId, refetch, showToast, t]);

  const handleMarkDeceased = useCallback((record: LivestockRecord) => {
    setDeceasedTarget(record);
  }, []);

  const handleDeceasedSaved = useCallback((record: LivestockRecord) => {
    setDeceasedTarget(null);
    void refetch();
    showToast(t('livestock.deceased.saved', { name: record.commonName }), 'success');
  }, [refetch, showToast, t]);

  const handleRestore = useCallback((record: LivestockRecord) => {
    // "Undo": active again; the API clears the date of death and the reason.
    patchLivestock(aquariumId, record.id, { status: 'active' })
      .then(() => {
        void refetch();
        showToast(t('livestock.deceased.restored', { name: record.commonName }), 'success');
      })
      .catch(() => {
        showToast(t('livestock.deceased.restoreError'), 'error');
      });
  }, [aquariumId, refetch, showToast, t]);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingRecord(null);
  }, []);

  // Reef Guard #1826: after adding a fish, coral or invertebrate, ask once about quarantine.
  const [quarantineAnimal, setQuarantineAnimal] = useState<QuarantineAnimal | null>(null);
  const handleFormSuccess = useCallback((created?: { id: string }, options?: LivestockFormSuccessOptions) => {
    setFormOpen(false);
    setEditingRecord(null);
    const category = options?.saved?.category;
    if (created?.id && options?.saved && category && category !== 'other') {
      setQuarantineAnimal({ id: created.id, name: options.saved.commonName, category });
    }
  }, []);

  const handleIdentifyOpen = useCallback(() => {
    setIsIdentifyOpen(true);
  }, []);

  const handleIdentifyClose = useCallback(() => {
    setIsIdentifyOpen(false);
  }, []);

  const handleIdentifySaved = useCallback((result?: { photoAttachFailed: boolean }) => {
    setIsIdentifyOpen(false);
    refetch();
    showToast(t(result?.photoAttachFailed ? 'livestock.identify.photoAttachFailed' : 'livestock.identify.saveSuccess'));
  }, [refetch, showToast, t]);

  const records = data ?? [];
  const isEmpty = !isLoading && !isError && records.length === 0;
  const showHeaderActions = !isError && !isEmpty;

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default" data-testid="livestock-page">
          <PageHeader
            title={t('livestock.title')}
            description={t('livestock.subtitle')}
            actions={
              showHeaderActions ? (
                <>
                  <Button variant="primary" onClick={handleAdd} disabled={isLoading}>
                    {t('livestock.add')}
                  </Button>
                  <Button
                    variant="secondary"
                    ref={identifyButtonRef}
                    onClick={handleIdentifyOpen}
                    disabled={isLoading}
                  >
                    {t('livestock.identifyFromPhoto')}
                  </Button>
                  <Button variant="secondary" onClick={openOverview} disabled={isLoading}>
                    {t('livestockOverview.entry')}
                  </Button>
                </>
              ) : undefined
            }
          />

          {overviewResult && (
            <Alert
              tone={overviewResult.photoAttachFailed > 0 ? 'warning' : 'success'}
              live="polite"
              onDismiss={dismissOverviewResult}
              dismissLabel={t('livestockOverview.result.dismiss')}
              data-testid="livestock-overview-result"
            >
              {t('livestockOverview.result.created', { count: overviewResult.created })}
              {overviewResult.photoAttachFailed > 0
                ? ` ${t('livestockOverview.result.photoFailed', { count: overviewResult.photoAttachFailed })}`
                : ''}
            </Alert>
          )}

          <Section title={t('livestock.section.all')}>

          <Grid minItemWidth="sm" gap="3">
            <Select
              data-testid="category-filter"
              value={categoryFilter}
              onChange={(e) =>
                setCategoryFilter(
                  e.target.value as 'coral' | 'fish' | 'invertebrate' | 'plant' | 'other' | '',
                )
              }
              aria-label={t('livestock.categoryFilter')}
            >
              <option value="">{t('livestock.allCategories')}</option>
              <option value="coral">{t('livestock.categories.coral')}</option>
              <option value="fish">{t('livestock.categories.fish')}</option>
              <option value="invertebrate">{t('livestock.categories.invertebrate')}</option>
              <option value="plant">{t('livestock.categories.plant')}</option>
              <option value="other">{t('livestock.categories.other')}</option>
            </Select>

            <Select
              data-testid="status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'active' | 'archived' | 'deceased' | 'quarantine' | '')}
              aria-label={t('livestock.statusFilter')}
            >
              <option value="">{t('livestock.allStatuses')}</option>
              <option value="active">{t('livestock.status.active')}</option>
              <option value="archived">{t('livestock.status.archived')}</option>
              <option value="deceased">{t('livestock.status.deceased')}</option>
              <option value="quarantine">{t('livestock.status.quarantine')}</option>
            </Select>

            <Select
              data-testid="sort-filter"
              value={sortFilter}
              onChange={(e) => setSortFilter(e.target.value as 'date_added' | 'common_name' | 'category' | '')}
              aria-label={t('livestock.sortBy')}
            >
              <option value="">{t('livestock.sortDefault')}</option>
              <option value="common_name">{t('livestock.sortName')}</option>
              <option value="date_added">{t('livestock.sortDate')}</option>
              <option value="category">{t('livestock.sortCategory')}</option>
            </Select>
          </Grid>

          {isLoading && (
            <Grid minItemWidth="sm" gap="4" data-testid="livestock-loading">
              <Card>
                <Stack gap="2">
                  <Text color="muted" aria-live="polite">
                    {t('livestock.loading')}
                  </Text>
                </Stack>
              </Card>
            </Grid>
          )}

          {isError && (
            <LoadErrorState
              data-testid="livestock-error"
              title={t('livestock.error.title')}
              titleLevel={3}
              message={t('livestock.error.body')}
              onRetry={() => void refetch()}
              retryLabel={t('livestock.error.retry')}
            />
          )}

          {isEmpty && (
            <Stack gap="4" align="center">
              <EmptyState
                icon={<Fish size={48} strokeWidth={1.5} />}
                headline={t('livestock.empty.title')}
                description={t('livestock.empty.body')}
                actionLabel={t('livestock.add')}
                actionVariant="primary"
                headingLevel={3}
                onAction={handleAdd}
              />
              <Inline gap="2" wrap justify="center">
                <Button variant="ghost" onClick={openOverview}>
                  {t('livestockOverview.entry')}
                </Button>
                <Button variant="ghost" ref={identifyButtonRef} onClick={handleIdentifyOpen}>
                  {t('livestock.identifyFromPhoto')}
                </Button>
              </Inline>
            </Stack>
          )}

          {!isLoading && !isError && records.length > 0 && (
            <LivestockList
              items={records}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onArchive={handleArchive}
              onMarkDeceased={handleMarkDeceased}
              onRestore={handleRestore}
            />
          )}
          </Section>

          <LivestockForm
            aquariumId={aquariumId}
            record={editingRecord}
            isOpen={formOpen}
            onClose={handleFormClose}
            onSuccess={handleFormSuccess}
          />

          <QuarantinePromptDialog
            aquariumId={aquariumId}
            animal={quarantineAnimal}
            onClose={() => {
              setQuarantineAnimal(null);
              refetch();
            }}
          />

          <MarkDeceasedDialog
            key={deceasedTarget?.id ?? 'none'}
            aquariumId={aquariumId}
            record={deceasedTarget}
            onClose={() => setDeceasedTarget(null)}
            onSaved={handleDeceasedSaved}
          />

          <LivestockIdentifyFlow
            aquariumId={aquariumId}
            isOpen={isIdentifyOpen}
            onClose={handleIdentifyClose}
            triggerRef={identifyButtonRef}
            onSaved={handleIdentifySaved}
          />
        </PageContent>
      }
    />
  );
}

export default LivestockPage;
